import { Capacitor } from "@capacitor/core";
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { firebaseConfig } from "shared/config/firebase";
import { registerDevice } from "../api";

const SERVICE_WORKER_URL = "/push-sw.js";

/**
 * Web push доступен только в браузере (в Capacitor-приложении нужны нативные пуши)
 * и только если для текущего окружения задан конфиг Firebase.
 * В Safari на iOS PushManager есть только у сайта, добавленного на экран «Домой».
 */
export const isWebPushAvailable = (): boolean =>
  Capacitor.getPlatform() === "web" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window &&
  !!firebaseConfig;

export const getWebPushPermission = (): NotificationPermission =>
  isWebPushAvailable() ? Notification.permission : "denied";

/**
 * Получает FCM-токен браузера и регистрирует его на бэкенде.
 * Вызывается при каждом запуске: бэкенд обновляет last_seen_at,
 * а если токен сменился — сохраняет новый.
 */
export const syncWebPushToken = async (): Promise<boolean> => {
  if (!isWebPushAvailable() || Notification.permission !== "granted") {
    return false;
  }

  if (!(await isSupported())) {
    return false;
  }

  const config = firebaseConfig!;

  const registration = await navigator.serviceWorker.register(
    SERVICE_WORKER_URL,
  );

  const app = initializeApp(config);

  const token = await getToken(getMessaging(app), {
    vapidKey: config.vapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    return false;
  }

  await registerDevice({ token, platform: "web" });

  return true;
};

/**
 * Запрашивает разрешение. Браузеры показывают запрос только в ответ
 * на действие пользователя, поэтому вызывать по нажатию кнопки.
 */
export const enableWebPush = async (): Promise<NotificationPermission> => {
  const permission = await Notification.requestPermission();

  if (permission === "granted") {
    await syncWebPushToken();
  }

  return permission;
};
